import { forwardRef, type CSSProperties, type ReactNode } from "react";
import MuiCard from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";

// antd <Card> -> MUI Card. title/extra -> CardHeader; children -> CardContent;
// bordered=false -> elevation card; size="small" -> denser padding.
export interface CardProps {
  title?: ReactNode;
  extra?: ReactNode;
  bordered?: boolean;
  size?: "default" | "small";
  bodyStyle?: CSSProperties;
  headStyle?: CSSProperties;
  hoverable?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
  onClick?: () => void;
  // antd Card forwards data-*/aria-* and other DOM attrs onto its root.
  [key: string]: unknown;
}

// forwardRef so consumers can measure/observe the card DOM node. ChatBox relies on
// this: it refs the card to compute a fill height; without ref forwarding the ref is
// null, the height calc never runs, and the chat stays stuck at its initial height.
export const CardBase = forwardRef<HTMLDivElement, CardProps>(function Card(
  { title, extra, bordered = true, size, bodyStyle, headStyle, children, style, className, onClick, ...rest },
  ref,
) {
  return (
    <MuiCard
      ref={ref}
      variant={bordered ? "outlined" : "elevation"}
      style={style}
      className={className}
      onClick={onClick}
      {...rest}
    >
      {(title != null || extra != null) && (
        <CardHeader
          title={title}
          action={extra}
          titleTypographyProps={{ variant: size === "small" ? "subtitle1" : "h6" }}
          sx={{ pb: 0 }}
          style={headStyle}
        />
      )}
      <CardContent sx={size === "small" ? { p: 1.5, "&:last-child": { pb: 1.5 } } : undefined} style={bodyStyle}>
        {children}
      </CardContent>
    </MuiCard>
  );
});

// antd <Card.Meta avatar title description> -> MUI CardHeader.
export interface CardMetaProps {
  avatar?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

const Meta = ({ avatar, title, description, style, className }: CardMetaProps) => (
  <CardHeader avatar={avatar} title={title} subheader={description} style={style} className={className} />
);

export const Card = Object.assign(CardBase, { Meta });

export default Card;
